import { api } from '../api/client';

import { API_CARD_DEFS_URL, API_COLLECTIONS_URL, API_ITEMS_URL } from '../utils';
import { noteCollectionsChanged } from '../contexts/CollectionsContext';

/**
 * Sub-collections: the writes (keepr-api docs/SCHEMA.md § Sub-collections).
 * Reads need nothing of their own — the family rides `GET
 * /collections/{id}` as `hierarchy` (utils/collectionFamily.js).
 *
 * Refusals carry a `code` and the API's own sentence (apiErrorMessage);
 * the few with detail a surface lists — `inherited_cards_in_use`, an item
 * move's `failures` — are read off `error.response.data` by the caller.
 */

/**
 * Makes a sub-collection of `parentId` (manage on the parent). With
 * `moveCardIds`, each of the parent's cards then moves down into it, one by
 * one: `{ insertedId, moved?: [{ cardId, moved: true, cards, itemTotal } |
 * { cardId, moved: false, code, message }] }`.
 */
export async function createSubCollection(parentId, { name, description, icon, color, moveCardIds = [] }) {
	const body = { name, parent_id: parentId };
	if (description) body.description = description;
	if (icon) body.icon = icon;
	if (color) body.color = color;
	if (moveCardIds.length) body.moveCardIds = moveCardIds;
	const { data } = await api.post(API_COLLECTIONS_URL, body);
	noteCollectionsChanged();
	return data || {};
}

/** Attaches `collectionId` beneath `parentId` (owner of both). */
export async function attachToParent(collectionId, parentId) {
	const { data } = await api.put(`${API_COLLECTIONS_URL}/${collectionId}/parent`, { parent_id: parentId });
	noteCollectionsChanged();
	return data || {};
}

/** Detaches a sub-collection from its parent (owner); 409 `inherited_cards_in_use` names what still depends on it. */
export async function detachFromParent(collectionId) {
	const { data } = await api.put(`${API_COLLECTIONS_URL}/${collectionId}/parent`, { parent_id: null });
	noteCollectionsChanged();
	return data || {};
}

/**
 * Moves a collection card (with its descendant cards owned by the same
 * collection, and their records) along the family line — down into a
 * sub-collection, or up into the collection above. `dryRun` writes nothing
 * and answers what would move: `{ direction, collection_id, cards: [{ _id,
 * name, itemCount }], itemTotal, needsALook }` (owner ruling D10: the web
 * shows this before anything moves).
 */
export async function moveCard(cardId, collectionId, { dryRun = false } = {}) {
	const { data } = await api.post(`${API_CARD_DEFS_URL}/${cardId}/move`, { collection_id: collectionId, ...(dryRun ? { dryRun: true } : {}) });
	return data || {};
}

/**
 * Moves up to 100 records to another collection of the same family, all or
 * nothing: `{ moved, ids, collection_id }`; 409 with `failures: [{ itemId,
 * code }]` and nothing moved.
 */
export async function moveItems(ids, collectionId) {
	const { data } = await api.post(`${API_ITEMS_URL}/move`, { ids, collection_id: collectionId });
	return data || {};
}

export const MAX_MOVE_ITEM_IDS = 100;
